import { z } from 'zod';
import { callFeaturesSchema } from './call.js';
import { environmentIdSchema, isoTimestampSchema, outcomeSchema } from './common.js';
import { assertionSchema, evidenceSchema, stepGroupSchema } from './evidence.js';
/**
 * The stages a run can cover. A run may execute one stage in isolation (handy
 * while debugging) or the whole chain, which is the normal end-to-end case.
 */
export const runStageSchema = z.enum(['api-health', 'sf-setup', 'live-call', 'post-call']);
export const RUN_STAGES = runStageSchema.options;
export const RUN_STAGE_LABELS = {
    'api-health': 'PBX API health',
    'sf-setup': 'Salesforce org setup',
    'live-call': 'Live call test',
    'post-call': 'Post-call verification',
};
/**
 * Inputs collected from the Salesforce Org Setup tab. Secrets are accepted here
 * but are never persisted in the clear: the server moves them into the encrypted
 * vault and stores only a reference on the run.
 */
export const runRequestSchema = z.object({
    environment: environmentIdSchema,
    stages: z.array(runStageSchema).min(1),
    salesforceOrgUrl: z
        .string()
        .url('Enter the full Salesforce org URL, for example https://acme.my.salesforce.com'),
    packageInstallUrl: z
        .string()
        .url('Enter the RocketPhone package installation URL')
        .optional()
        .or(z.literal('')),
    /** External Credential principal used by the Named Credential callouts. */
    clientId: z.string().min(1).optional().or(z.literal('')),
    clientSecret: z.string().min(1).optional().or(z.literal('')),
    tenantCode: z.string().min(1, 'Tenant code is required'),
    /** Which of the agent's numbers to place the call from. */
    agentNumber: z.string().min(1).optional().or(z.literal('')),
    /** Number the bot dials to create the test call. */
    destinationNumber: z.string().min(1).optional().or(z.literal('')),
    /**
     * A spare number on the tenant that the run routes to the temporary customer
     * phone and uses as its caller ID. With it, both directions go through the
     * PBX the way a real call does; without it the customer phone only has a
     * short extension, which the PBX does not route from a softphone.
     */
    customerNumber: z.string().min(1).optional().or(z.literal('')),
    /** The Google Voice number, when Google Voice plays the customer. */
    googleVoiceNumber: z.string().min(1).optional().or(z.literal('')),
    runAsUsername: z.string().optional().or(z.literal('')),
    userProfile: z.string().default('System Administrator'),
    options: z
        .object({
        /**
         * `reinstall` uninstalls first then installs and configures.
         * `upgrade` only submits a PackageInstallRequest against the version
         * already in the org — no uninstall, no org configuration.
         */
        packageAction: z.enum(['reinstall', 'upgrade']).default('reinstall'),
        /** Uninstall any existing package version before installing. */
        uninstallFirst: z.boolean().default(true),
        /**
         * Allow clearing RocketPhone Task data via the API when uninstall is
         * blocked by referencing records.
         */
        allowDestructiveCleanup: z.boolean().default(true),
        /**
         * Place Lightning components by deploying FlexiPage metadata. Turn off to
         * try Lightning App Builder drag-and-drop instead.
         */
        allowFlexiPageFallback: z.boolean().default(true),
        /** Plan the run and validate inputs without touching any live system. */
        dryRun: z.boolean().default(false),
        /** Seconds of conversation before the bot hangs up. */
        callDurationSeconds: z.number().int().min(30).max(900).default(180),
        /**
         * `outbound`: the agent softphone dials the customer number (or the
         * destination number when there is no customer phone).
         * `inbound`: the customer phone dials the agent number through the PBX
         * and the agent softphone answers.
         * Existing PBX users, extensions and tenants are never changed; a
         * temporary customer phone and its number routing are removed after the call.
         */
        callDirection: z.enum(['outbound', 'inbound']).default('outbound'),
        /**
         * What plays the customer. `pbx-extension` adds a temporary RocketPBX
         * softphone; `google-voice` drives voice.google.com with the saved Google
         * Voice session, so the call crosses the public phone network.
         */
        customerPhone: z.enum(['pbx-extension', 'google-voice']).default('pbx-extension'),
        /** Keep the browser visible; only meaningful for local development. */
        headed: z.boolean().default(false),
        /**
         * Which in-call panels to exercise. Connecting the call is always in
         * scope. Defaults to every feature so existing runs keep today's
         * behaviour. Turn them all off to prove only that a call connects.
         */
        callFeatures: callFeaturesSchema.default({
            'live-transcript': true,
            'live-bookmarks': true,
            'call-assist': true,
            'question-assist': true,
            'rocket-form': true,
            checklist: true,
        }),
    })
        .default({}),
    label: z.string().optional(),
});
export const runStepSchema = z.object({
    id: z.string(),
    runId: z.string(),
    stepKey: z.string(),
    title: z.string(),
    description: z.string().optional(),
    group: stepGroupSchema,
    stage: runStageSchema,
    position: z.number().int().nonnegative(),
    outcome: outcomeSchema,
    startedAt: isoTimestampSchema.optional(),
    endedAt: isoTimestampSchema.optional(),
    durationMs: z.number().int().nonnegative().optional(),
    summary: z.string().optional(),
    reason: z.string().optional(),
    errorDetail: z.string().optional(),
    assertions: z.array(assertionSchema).default([]),
    evidence: z.array(evidenceSchema).default([]),
});
export const runSchema = z.object({
    id: z.string(),
    label: z.string().optional(),
    environment: environmentIdSchema,
    stages: z.array(runStageSchema),
    outcome: outcomeSchema,
    createdAt: isoTimestampSchema,
    startedAt: isoTimestampSchema.optional(),
    endedAt: isoTimestampSchema.optional(),
    durationMs: z.number().int().nonnegative().optional(),
    tenantCode: z.string(),
    salesforceOrgUrl: z.string(),
    dryRun: z.boolean().default(false),
    containerId: z.string().optional(),
    reason: z.string().optional(),
    /** Counts used by the dashboard without loading the whole step tree. */
    totals: z
        .object({
        steps: z.number().int().nonnegative(),
        passed: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
        warning: z.number().int().nonnegative(),
        skipped: z.number().int().nonnegative(),
    })
        .optional(),
});
export const runDetailSchema = runSchema.extend({
    steps: z.array(runStepSchema),
    request: runRequestSchema.partial().optional(),
});
//# sourceMappingURL=run.js.map